<template>
	<div>
		<!-- Loading state while fetching project data -->
		<div v-if="isLoading" class="flex justify-center items-center p-8">
			<span>Loading utilities configuration...</span>
		</div>

		<!-- Show multi-step component when isSingleUtilitiesChecked is false -->
		<assessmentTabUtilitiesMultiStep
			v-else-if="!isSingleUtilitiesMode"
			v-bind="$attrs"
			:assessmentId="assessmentId"
			:tabName="tabName"
			:subTabName="subTabName"
			:stepId="stepId"
			:assessment="assessment"
		/>

		<!-- Show single-step component when isSingleUtilitiesChecked is true -->
		<assessmentTabUtilitiesSingleStep
			v-else
			v-bind="$attrs"
			:assessmentId="assessmentId"
			:tabName="tabName"
			:subTabName="subTabName"
			:stepId="stepId"
			:assessment="assessment"
		/>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue"
import { ApiClient, Assessment, Project } from "../../entities/NSwagGenerated"
import assessmentTabUtilitiesMultiStep from "./assessmentTabUtilitiesMultiStep.vue"
import assessmentTabUtilitiesSingleStep from "./assessmentTabUtilitiesSingleStep.vue"

const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())

const props = defineProps({
	assessmentId: {
		type: String,
		required: true,
		default: "",
	},
	tabName: {
		type: String,
		required: true,
		default: "",
	},
	subTabName: {
		type: String,
		required: true,
		default: "",
	},
	stepId: {
		type: String,
		required: false,
		default: "",
	},
	assessment: {
		type: Object as () => Assessment,
		required: false,
	},
})

const project = ref<Project | null>(null)
const isSingleUtilitiesMode = ref<boolean>(false)
const isLoading = ref<boolean>(true)

// Get the project information to check the isSingleUtilitiesChecked flag
onMounted(async () => {
	try {
		// Method 1: Use the optimized API that returns assessment with project data
		const assessmentData = await apiClient.getAssessmentWithProject(props.assessmentId)

		// Method 2: Also check using the dedicated API as a verification
		const singleModeCheck = await apiClient.checkSingleUtilitiesMode(props.assessmentId)

		if (assessmentData?.subProject?.project) {
			project.value = assessmentData.subProject.project
			const projectFlag = assessmentData.subProject.project.isSingleUtilitiesChecked

			// Use the project's isSingleUtilitiesChecked flag
			// true (1) = single step, false (0) = multi step
			isSingleUtilitiesMode.value = Boolean(projectFlag)

		} else {
			// Fallback: use the direct API result
			isSingleUtilitiesMode.value = Boolean(singleModeCheck)
			console.log(
				`[Utilities] Using fallback API result: ${singleModeCheck} → Using ${isSingleUtilitiesMode.value ? "SingleStep" : "MultiStep"} component`
			)
		}
	} catch (error) {
		console.error("Error loading project data:", error)
		// Default to multi-step mode if there's an error
		isSingleUtilitiesMode.value = false
	} finally {
		isLoading.value = false
	}
})
</script>
